// The docs table of contents: sections → pages, in reading order. This is the
// single source for the TOC rail, routing (/docs/:slug), the filter (title +
// blurb + keywords), and prev/next links. Pages are task-named ("Clone a
// vault…", not "The clone command") so someone scanning for what they're
// trying to DO finds it by title alone.

import type { ComponentType } from "react";
import { QuickstartDoc } from "./pages/quickstart";
import { InstallCliDoc } from "./pages/install-cli";
import { MobileSetupDoc } from "./pages/mobile-setup";
import { CreateVaultDoc } from "./pages/create-vault";
import { CloneVaultDoc } from "./pages/clone-vault";
import { LinkFolderDoc } from "./pages/link-folder";
import { MembersDoc } from "./pages/members";
import { VersionHistoryDoc } from "./pages/version-history";
import { RemoveVaultDoc } from "./pages/remove-vault";
import { WebAppDoc } from "./pages/web-app";
import { AccountSecurityDoc } from "./pages/account-security";
import { InviteUsersDoc } from "./pages/invite-users";
import { RolesDoc } from "./pages/roles";
import { ServerMaintenanceDoc } from "./pages/server-maintenance";
import { CliReferenceDoc } from "./pages/cli-reference";
import { ConceptsDoc } from "./pages/concepts";

export interface DocPage {
  slug: string;
  title: string;
  /** Short label for the TOC rail when the full title would wrap — every
   *  rail entry must stay one line so the pill highlight keeps its shape. */
  navTitle?: string;
  /** One sentence under the page title; also matched by the TOC filter. */
  blurb: string;
  /** Extra filter terms a user might type that aren't in the title/blurb. */
  keywords?: string[];
  Component: ComponentType;
}

export interface DocSectionEntry {
  label: string;
  pages: DocPage[];
}

export const DOC_SECTIONS: DocSectionEntry[] = [
  {
    label: "Get started",
    pages: [
      {
        slug: "quickstart",
        title: "Quick start",
        blurb: "From zero to a synced vault in Obsidian: install the CLI, sign in, clone.",
        keywords: ["setup", "install", "begin", "new", "onboarding"],
        Component: QuickstartDoc,
      },
      {
        slug: "install-cli",
        title: "Install & update the CLI",
        blurb: "Installing cairn, signing in and out, and keeping everything current.",
        keywords: ["login", "logout", "whoami", "update", "terminal", "curl"],
        Component: InstallCliDoc,
      },
      {
        slug: "mobile-setup",
        title: "Set up a phone or tablet",
        blurb: "Pair a mobile device with a QR code and pick a vault to sync.",
        keywords: ["ios", "android", "iphone", "ipad", "qr", "pair", "pairing", "phone", "brat", "install", "pat", "token"],
        Component: MobileSetupDoc,
      },
    ],
  },
  {
    label: "Vaults",
    pages: [
      {
        slug: "create-vault",
        title: "Create a vault",
        blurb: "Start a new vault from the web app or the CLI.",
        keywords: ["new vault"],
        Component: CreateVaultDoc,
      },
      {
        slug: "clone-vault",
        title: "Clone a vault to a device",
        blurb: "Bring an existing vault onto another machine.",
        keywords: ["download", "second device", "another computer", "checkout"],
        Component: CloneVaultDoc,
      },
      {
        slug: "link-folder",
        title: "Link an existing folder",
        blurb: "Attach notes you already have to a vault, with a safe previewed merge.",
        keywords: ["import", "attach", "join", "merge", "existing notes", "unlink"],
        Component: LinkFolderDoc,
      },
      {
        slug: "members",
        title: "Share a vault & manage members",
        navTitle: "Sharing & members",
        blurb: "Add people to a vault and control what they can do.",
        keywords: ["share", "collaborate", "permissions", "owner", "editor", "viewer", "team"],
        Component: MembersDoc,
      },
      {
        slug: "version-history",
        title: "Version history & restore",
        blurb: "Browse old versions of a note and bring one back.",
        keywords: ["undo", "recover", "revert", "backup", "diff", "restore", "lost"],
        Component: VersionHistoryDoc,
      },
      {
        slug: "remove-vault",
        title: "Unlink, leave, or delete",
        blurb: "The three ways to remove a vault, and what each one affects.",
        keywords: ["remove", "delete", "stop syncing", "quit"],
        Component: RemoveVaultDoc,
      },
    ],
  },
  {
    label: "Web app",
    pages: [
      {
        slug: "web-app",
        title: "Browse in the web app",
        blurb: "Live read-only vaults in the browser: tree, notes, presence, status.",
        keywords: ["browser", "read-only", "viewer", "presence", "sync status", "offline"],
        Component: WebAppDoc,
      },
    ],
  },
  {
    label: "Account",
    pages: [
      {
        slug: "account-security",
        title: "Account & security",
        blurb: "Display name, devices & sessions, two-factor, passwords.",
        keywords: ["totp", "2fa", "password", "sessions", "devices", "revoke", "profile"],
        Component: AccountSecurityDoc,
      },
    ],
  },
  {
    label: "Administration",
    pages: [
      {
        slug: "invite-users",
        title: "Invite & manage users",
        blurb: "Onboard new accounts and administer existing ones.",
        keywords: ["onboard", "invite link", "disable", "reset password", "delete user"],
        Component: InviteUsersDoc,
      },
      {
        slug: "roles",
        title: "Roles & permissions",
        blurb: "What admin, manager, member, and guest can each do.",
        keywords: ["admin", "manager", "guest", "access", "permission matrix"],
        Component: RolesDoc,
      },
      {
        slug: "server-maintenance",
        title: "Server maintenance",
        blurb: "Stats, garbage collection, compaction, quotas, retention.",
        keywords: ["gc", "compaction", "quota", "stats", "storage", "retention", "ops"],
        Component: ServerMaintenanceDoc,
      },
    ],
  },
  {
    label: "Reference",
    pages: [
      {
        slug: "cli-reference",
        title: "CLI reference",
        blurb: "Every cairn command, what it does, and who can run it.",
        keywords: ["commands", "flags", "terminal", "man page"],
        Component: CliReferenceDoc,
      },
      {
        slug: "concepts",
        title: "How Cairn works",
        blurb: "Plain files, conflict-free merging, offline-first, self-hosted.",
        keywords: ["crdt", "sync", "architecture", "encryption", "conflict", "offline", "trust"],
        Component: ConceptsDoc,
      },
    ],
  },
];

/** All pages in reading order (for prev/next and slug lookup). */
export const ALL_DOCS: DocPage[] = DOC_SECTIONS.flatMap((s) => s.pages);

export function findDoc(slug: string): DocPage | undefined {
  return ALL_DOCS.find((p) => p.slug === slug);
}

/** The section a page belongs to (for the page header eyebrow). */
export function sectionOf(slug: string): DocSectionEntry | undefined {
  return DOC_SECTIONS.find((s) => s.pages.some((p) => p.slug === slug));
}
